import { useEffect, useState } from "hono/jsx";

// Inline Icons for X, Ban, Plane
const IconX = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 18 18" />
  </svg>
);
const IconBan = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m4.9 4.9 14.2 14.2" />
  </svg>
);
const IconPlane = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="M2 12h20" />
    <path d="M13 19l4-7-4-7" />
  </svg>
  // Actually a better plane icon
  // <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h20"/><path d="M13 19l4-7-4-7"/></svg>
  // Waiting... let's just use simple shapes or generic SVGs
);

interface Player {
  id: string;
  name?: string;
  color: string;
  isHost: boolean;
  canFly?: boolean;
  isMe: boolean;
}

interface PlayerListProps {
  // Props not used directly, relying on events
}

export function PlayerList() {
  const [visible, setVisible] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [isHost, setIsHost] = useState(false);
  const [tab, setTab] = useState<"players" | "bans">("players");
  // deno-lint-ignore no-explicit-any
  const [bannedList, setBannedList] = useState<{ ips: any[]; users: any[] }>({
    ips: [],
    users: [],
  });

  useEffect(() => {
    const onToggle = (e: CustomEvent) => setVisible(e.detail);
    const onUpdate = (e: CustomEvent) => setPlayers(e.detail);
    const onHostUpdate = (e: CustomEvent) => setIsHost(e.detail);
    // deno-lint-ignore no-explicit-any
    const onBansUpdate = (e: CustomEvent) => setBannedList(e.detail);

    document.addEventListener(
      "vox:playerlist:toggle",
      onToggle as EventListener,
    );
    document.addEventListener("vox:players:update", onUpdate as EventListener);
    document.addEventListener("vox:host:update", onHostUpdate as EventListener);
    document.addEventListener(
      "vox:bannedlist:update",
      onBansUpdate as EventListener,
    );

    return () => {
      document.removeEventListener(
        "vox:playerlist:toggle",
        onToggle as EventListener,
      );
      document.removeEventListener(
        "vox:players:update",
        onUpdate as EventListener,
      );
      document.removeEventListener(
        "vox:host:update",
        onHostUpdate as EventListener,
      );
      document.removeEventListener(
        "vox:bannedlist:update",
        onBansUpdate as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    // Sync with game state
    if (visible) {
      document.exitPointerLock();
      document.dispatchEvent(new CustomEvent("vox:menu:opened"));
    } else {
      // Notify game to resume inputs
      document.dispatchEvent(new CustomEvent("vox:menu:closed"));
    }
  }, [visible]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (visible && e.code === "Escape") {
        setVisible(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible]);

  useEffect(() => {
    if (visible && tab === "bans") {
      document.dispatchEvent(new CustomEvent("vox:request:bans"));
    }
  }, [visible, tab]);

  if (!visible) return null;

  const toggleFlight = (id: string) => {
    document.dispatchEvent(
      new CustomEvent("vox:toggle:flight", { detail: id }),
    );
  };

  const unban = (target: string, type: "ip" | "user") => {
    document.dispatchEvent(
      new CustomEvent("vox:unban:player", { detail: { target, type } }),
    );
  };

  const [banTarget, setBanTarget] = useState<
    { id: string; name: string } | null
  >(null);
  const [banOptions, setBanOptions] = useState({ banIp: false, global: false });

  const confirmBan = () => {
    if (!banTarget) return;
    document.dispatchEvent(
      new CustomEvent("vox:kick:player", {
        detail: {
          targetId: banTarget.id,
          banIp: banOptions.banIp,
          banUser: true, // Always ban user (per request: "just bans the user from the room")
          global: banOptions.global,
        },
      }),
    );
    setBanTarget(null);
  };

  return (
    <div
      id="player-list-modal"
      className="fixed inset-0 w-screen h-screen flex items-center justify-center pointer-events-none z-50"
    >
      <div className="bg-black/90 text-white rounded-xl p-6 min-w-[500px] pointer-events-auto backdrop-blur-md border border-white/10 shadow-2xl relative">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold">Manager</h2>
            {isHost && (
              <div className="flex bg-white/10 rounded-lg p-1">
                <button
                  onClick={() => setTab("players")}
                  className={`px-3 py-1 rounded text-sm font-bold transition-all ${
                    tab === "players"
                      ? "bg-white text-black"
                      : "text-white/50 hover:text-white"
                  }`}
                >
                  Players ({players.length})
                </button>
                <button
                  onClick={() => setTab("bans")}
                  className={`px-3 py-1 rounded text-sm font-bold transition-all ${
                    tab === "bans"
                      ? "bg-white text-black"
                      : "text-white/50 hover:text-white"
                  }`}
                >
                  Bans
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setVisible(false)}
            className="p-1 hover:bg-white/10 rounded text-white/50 hover:text-white transition-colors"
          >
            <IconX />
          </button>
        </div>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
          {tab === "players" &&
            players.map((p) => (
              <div
                key={p.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  p.isMe ? "bg-white/10" : "bg-white/5"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full shadow-[0_0_10px_currentColor]"
                    style={{ color: p.color, backgroundColor: p.color }}
                  />
                  <span
                    className={`font-medium ${
                      p.isMe ? "text-white" : "text-white/80"
                    }`}
                  >
                    {p.name} {p.isMe && "(You)"}
                  </span>
                  {p.isHost && (
                    <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-bold border border-yellow-500/30">
                      HOST
                    </span>
                  )}
                  {p.canFly && (
                    <span
                      className="ml-1 text-xs bg-blue-500/20 text-blue-400 px-1 rounded border border-blue-500/30"
                      title="Flight Enabled"
                    >
                      ✈️
                    </span>
                  )}
                </div>

                {isHost && !p.isMe && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleFlight(p.id)}
                      title={p.canFly ? "Disable Flight" : "Enable Flight"}
                      className={`p-1.5 rounded transition-colors ${
                        p.canFly
                          ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/40"
                          : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <IconPlane />
                    </button>
                    <div className="w-px h-4 bg-white/10 mx-1" />
                    <button
                      onClick={() => {
                        setBanOptions({ banIp: false, global: false });
                        setBanTarget({ id: p.id, name: p.name || "Unknown" });
                      }}
                      title="Kick/Ban Player..."
                      className="p-1.5 rounded bg-white/5 text-white/40 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                    >
                      <IconBan />
                    </button>
                  </div>
                )}
              </div>
            ))}

          {tab === "bans" && (
            <div className="space-y-4">
              {/* Active Bans List */}
              {bannedList.users.length === 0 && bannedList.ips.length === 0 && (
                <div className="text-center p-8 text-white/30 italic">
                  No active bans.
                </div>
              )}

              {bannedList.users.map((b: any) => (
                <div
                  key={b.userId}
                  className="flex items-center justify-between p-3 rounded-lg bg-red-900/10 border border-red-500/20"
                >
                  <div className="flex flex-col">
                    <span className="font-bold text-red-200">{b.username}</span>
                    <span className="text-[10px] text-red-400/50 uppercase tracking-wider">
                      User Ban
                    </span>
                  </div>
                  <button
                    onClick={() => unban(b.userId, "user")}
                    className="px-3 py-1 rounded bg-white/5 hover:bg-white/10 text-xs font-bold text-white/60 hover:text-white transition-colors"
                  >
                    Unban
                  </button>
                </div>
              ))}

              {bannedList.ips.map((b: any) => (
                <div
                  key={b.ip}
                  className="flex items-center justify-between p-3 rounded-lg bg-red-900/10 border border-red-500/20"
                >
                  <div className="flex flex-col">
                    <span className="font-bold text-red-200">
                      {b.username} (IP)
                    </span>
                    <span className="text-[10px] text-red-400/50 uppercase tracking-wider">
                      IP: {b.ip}
                    </span>
                  </div>
                  <button
                    onClick={() => unban(b.ip, "ip")}
                    className="px-3 py-1 rounded bg-white/5 hover:bg-white/10 text-xs font-bold text-white/60 hover:text-white transition-colors"
                  >
                    Unban
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/40 flex justify-between">
          <span>
            Press{" "}
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60 font-mono">
              `
            </kbd>{" "}
            or{" "}
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white/60 font-mono">
              Tab
            </kbd>{" "}
            to close
          </span>
          <span>{players.length} online</span>
        </div>

        {/* Ban Modal */}
        {banTarget && (
          <div className="absolute inset-0 bg-black/95 rounded-xl flex flex-col items-center justify-center p-6 z-50">
            <h3 className="text-xl font-bold text-red-400 mb-4">
              Remove {banTarget.name}?
            </h3>

            <div className="space-y-3 w-full max-w-[200px] mb-6">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={banOptions.banIp}
                  onChange={(e) =>
                    setBanOptions((o) => ({
                      ...o,
                      banIp: (e.currentTarget as HTMLInputElement).checked,
                    }))}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 checked:bg-red-500 checked:border-red-500"
                />
                <span className="text-white/80 group-hover:text-white">
                  Ban IP Address
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={banOptions.global}
                  onChange={(e) =>
                    setBanOptions((o) => ({
                      ...o,
                      global: (e.currentTarget as HTMLInputElement).checked,
                    }))}
                  className="w-5 h-5 rounded border-white/20 bg-white/5 checked:bg-red-500 checked:border-red-500"
                />
                <span className="text-white/80 group-hover:text-white">
                  Global Ban
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setBanTarget(null)}
                className="px-4 py-2 rounded bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBan}
                className="px-4 py-2 rounded bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white border border-red-500/50 transition-colors font-bold"
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
